/*
  File: cart-state.js
  Purpose: Local in-memory mirror of Shopify's server-side cart. The
  authoritative source remains the server (/cart.js, /cart/add.js,
  /cart/change.js); this module is a projection used to drive optimistic
  UI without conflating the rendered DOM with the cart data itself.

  Architecture (pragmatic intermediate; full rework deferred — see
  context/checkout-friction-reduction/cart-architecture-rework-deferred.md):

    Server cart  →  cartState  →  subscribers (render)  →  DOM
                       ↑
                   mutators
              (addLine / setQtyByKey /
               replaceFromServer)

  Mutators are the ONLY way to change state. Subscribers receive the
  new state on every change and update DOM consumers (e.g. footer
  subtotal, checkout button label). The DOM is read-only output —
  never read back as truth.

  Hydration: on first getState() call, this module reads a JSON blob
  emitted by the cart-drawer template (#cart-bootstrap-state). If
  absent (e.g. on a page that doesn't render the cart drawer), it
  falls back to a synchronous initial state with totalPriceCents 0
  and lines empty; the next server response (replaceFromServer) will
  fill it in.
*/

(function (global) {
  'use strict';

  /** @typedef {{
   *   key: string,
   *   variantId: number,
   *   qty: number,
   *   unitPriceCents: number,
   *   lineTotalCents: number,
   *   title: string,
   *   isApparel: boolean,
   *   size: string,
   * }} CartLine */

  // Sticky per-variant metadata (isApparel, size). Sourced from the
  // Liquid bootstrap blob, which sees product.category and the variant's
  // Size option. /cart.js responses don't carry these fields, so we
  // remember them across refreshes keyed by variantId. Cleared only on
  // page reload — variant metadata doesn't change at runtime.
  const variantMeta = Object.create(null);

  /** @typedef {{
   *   key: string,           // dedupe identity — code (for codes), or 'auto:<title>' otherwise
   *   title: string,         // display string for the chip
   *   type: 'discount_code'|'automatic'|'manual'|'script',
   *   targetType: 'line_item'|'shipping_line',
   *   amountCents: number,   // total_allocated_amount
   *   removable: boolean,    // true only for type='discount_code' && targetType='line_item'
   * }} AppliedDiscount */

  /** @typedef {{
   *   hydrated: boolean,
   *   totalPriceCents: number,
   *   itemCount: number,
   *   lines: CartLine[],
   *   appliedDiscounts: AppliedDiscount[],
   *   appliedDiscountCodes: string[],   // derived: codes only, uppercase. Kept for membership/cap.
   *   totalDiscountsCents: number,      // excludes shipping_line so it matches displayed subtotal
   * }} CartState */

  // Cap on simultaneously-applied codes. AJAX cart supports multi-code
  // natively via comma-separated `discount=CODE1,CODE2` (verified against
  // Shopify Cart API docs). Stacking on the cart still requires each code
  // to be combinable in admin AND the store to be on Shopify Advanced+ for
  // combinable order discounts — Shopify will silently honor only the
  // first/most-favorable if those conditions aren't met. The cap here is
  // a UX guard, not an API constraint.
  const MAX_DISCOUNT_CODES = 4;

  /** @type {CartState} */
  let state = {
    hydrated: false,
    totalPriceCents: 0,
    itemCount: 0,
    lines: [],
    appliedDiscounts: [],
    appliedDiscountCodes: [],
    totalDiscountsCents: 0,
  };

  const subscribers = new Set();

  function freeze(s) {
    return Object.freeze({
      hydrated: s.hydrated,
      totalPriceCents: s.totalPriceCents,
      itemCount: s.itemCount,
      lines: Object.freeze(s.lines.map((l) => Object.freeze({ ...l }))),
      appliedDiscounts: Object.freeze(s.appliedDiscounts.map((d) => Object.freeze({ ...d }))),
      appliedDiscountCodes: Object.freeze(s.appliedDiscountCodes.slice()),
      totalDiscountsCents: s.totalDiscountsCents,
    });
  }

  function emit() {
    const snapshot = freeze(state);
    subscribers.forEach((cb) => {
      try { cb(snapshot); } catch (e) { console.error('[cart-state] subscriber threw', e); }
    });
  }

  function recompute() {
    state.totalPriceCents = state.lines.reduce((sum, l) => sum + l.lineTotalCents, 0);
    state.itemCount = state.lines.reduce((sum, l) => sum + l.qty, 0);
  }

  function hydrateFromBootstrap() {
    const blob = document.getElementById('cart-bootstrap-state');
    if (!blob) return false;
    try {
      const parsed = JSON.parse(blob.textContent || '{}');
      replaceFromServerInternal(parsed);
      return true;
    } catch (e) {
      console.error('[cart-state] failed to parse bootstrap blob', e);
      return false;
    }
  }

  function ensureHydrated() {
    if (state.hydrated) return;
    if (hydrateFromBootstrap()) return;
    // Bootstrap blob missing — leave state empty until first
    // replaceFromServer call. This is fine on pages that don't render
    // the cart drawer; the first cart action will populate state.
  }

  /**
   * Replace the entire cart state from a Shopify cart JSON payload
   * (the shape returned by /cart.js, /cart/add.js, /cart/change.js,
   * or the bootstrap Liquid blob).
   * @param {object} parsed
   */
  function replaceFromServerInternal(parsed) {
    if (!parsed || typeof parsed !== 'object') return;
    const items = Array.isArray(parsed.items) ? parsed.items : [];
    state.lines = items.map((item) => {
      const qty = Number(item.quantity) || 0;
      const lineTotalCents = Number(item.final_line_price);
      const unitPriceCents = qty > 0 ? Math.round(lineTotalCents / qty) : 0;
      const variantId = Number(item.variant_id || item.id) || 0;
      // Refresh sticky map when the bootstrap blob carries the fields;
      // /cart.js responses don't, so fall back to whatever we already know.
      if (variantId && Object.prototype.hasOwnProperty.call(item, 'is_apparel')) {
        variantMeta[variantId] = {
          isApparel: Boolean(item.is_apparel),
          size: String(item.size || ''),
        };
      }
      const meta = variantMeta[variantId] || { isApparel: false, size: '' };
      return {
        key: String(item.key || ''),
        variantId: variantId,
        qty: qty,
        unitPriceCents: unitPriceCents,
        lineTotalCents: lineTotalCents,
        title: String(item.title || ''),
        isApparel: meta.isApparel,
        size: meta.size,
      };
    });
    // Build appliedDiscounts (full chip-render data) from
    // cart.discount_applications. Covers all four Shopify discount types
    // (discount_code, automatic, manual, script) and both target_types
    // (line_item, shipping_line). Chips render for everything; only
    // discount_code line-item entries get a removable × button.
    //
    // cart.discount_applications is canonical — works even for discounts
    // that allocate only at the line level (e.g. a code that grants a
    // free product), which is why a chip wouldn't render through the
    // older cart.discount_codes path.
    const apps = Array.isArray(parsed.discount_applications) ? parsed.discount_applications : [];
    state.appliedDiscounts = apps
      .map((a) => {
        if (!a) return null;
        const type = String(a.type || '');
        const targetType = String(a.target_type || 'line_item');
        const title = String(a.title || a.code || '').trim();
        if (!title) return null;
        const isCode = type === 'discount_code';
        const removable = isCode && targetType !== 'shipping_line';
        const key = isCode ? title.toUpperCase() : type + ':' + title;
        return {
          key: key,
          title: title,
          type: type || 'automatic',
          targetType: targetType,
          amountCents: Number(a.total_allocated_amount) || 0,
          removable: removable,
        };
      })
      .filter(Boolean);

    // Codes-only mirror, used for membership / cap checks during apply
    // (manual entry's "already_applied", v1's MAX_DISCOUNT_CODES guard).
    state.appliedDiscountCodes = state.appliedDiscounts
      .filter((d) => d.type === 'discount_code')
      .map((d) => d.key);

    // If discount_applications didn't populate (older response shape),
    // fall back to cart.discount_codes for codes-only display.
    if (!state.appliedDiscounts.length) {
      const rawCodes = Array.isArray(parsed.discount_codes) ? parsed.discount_codes : [];
      const fallbackCodes = rawCodes
        .map((d) => {
          if (typeof d === 'string') return d;
          if (d && typeof d === 'object' && d.applicable !== false) return String(d.code || '');
          return '';
        })
        .filter(Boolean)
        .map((c) => c.toUpperCase());
      state.appliedDiscounts = fallbackCodes.map((code) => ({
        key: code,
        title: code,
        type: 'discount_code',
        targetType: 'line_item',
        amountCents: 0,
        removable: true,
      }));
      state.appliedDiscountCodes = fallbackCodes;
    }

    // Total savings shown in the "You saved" header — must MATCH the
    // displayed subtotal math. cart.total_price doesn't include shipping
    // (Shopify computes shipping at checkout), so shipping_line discount
    // allocations would inflate the savings header without being
    // reflected in any visible subtotal. Sum non-shipping allocations
    // only. Falls back to original-total when discount_applications is
    // missing.
    const sumNonShipping = state.appliedDiscounts
      .filter((d) => d.targetType !== 'shipping_line')
      .reduce((acc, d) => acc + d.amountCents, 0);

    if (sumNonShipping > 0) {
      state.totalDiscountsCents = sumNonShipping;
    } else {
      const originalTotalCents = Number(parsed.original_total_price);
      const totalPriceCents = Number(parsed.total_price || 0);
      state.totalDiscountsCents = Number.isFinite(originalTotalCents)
        ? Math.max(0, originalTotalCents - totalPriceCents)
        : 0;
    }
    state.hydrated = true;
    recompute();
  }

  function replaceFromServer(parsed) {
    replaceFromServerInternal(parsed);
    emit();
  }

  /**
   * Apply an optimistic add. The line gets a temp key
   * `optimistic:<variantId>` and is appended; replaceFromServer will
   * supersede it with the server-assigned key.
   * @param {{variantId:number, qty:number, unitPriceCents:number, title?:string}} input
   */
  function addLine(input) {
    ensureHydrated();
    const variantId = Number(input.variantId) || 0;
    const qty = Number(input.qty) || 1;
    const unitPriceCents = Number(input.unitPriceCents) || 0;
    if (!variantId || !unitPriceCents) return;

    // If the variant is already in the cart, bump its qty rather than
    // adding a duplicate line. This matches Shopify's server behavior.
    const existing = state.lines.find((l) => l.variantId === variantId);
    if (existing) {
      existing.qty += qty;
      existing.lineTotalCents = existing.unitPriceCents * existing.qty;
    } else {
      const meta = variantMeta[variantId] || { isApparel: false, size: '' };
      state.lines.push({
        key: 'optimistic:' + variantId,
        variantId: variantId,
        qty: qty,
        unitPriceCents: unitPriceCents,
        lineTotalCents: unitPriceCents * qty,
        title: String(input.title || ''),
        isApparel: meta.isApparel,
        size: meta.size,
      });
    }
    recompute();
    emit();
  }

  /**
   * Set the qty for the line identified by stable `key` (Shopify line
   * item key, format `<variantId>:<propertiesHash>`). qty:0 removes
   * the line. Key-based addressing survives cart reorders / mid-flight
   * mutations of other lines.
   * @param {string} key
   * @param {number} qty
   */
  function setQtyByKey(key, qty) {
    ensureHydrated();
    if (!key) return;
    const idx = state.lines.findIndex((l) => l.key === key);
    if (idx === -1) return;
    const newQty = Math.max(0, Number(qty) || 0);
    const line = state.lines[idx];
    if (newQty === 0) {
      state.lines.splice(idx, 1);
    } else {
      line.qty = newQty;
      line.lineTotalCents = line.unitPriceCents * newQty;
    }
    recompute();
    emit();
  }

  /**
   * Apply a discount code via the AJAX cart. Optimistically pushes the
   * code into appliedDiscountCodes, POSTs to /cart/update.js, and
   * reconciles state from the server response. Returns a Promise that
   * resolves to {ok, code, reason?}: ok=true if the code stuck (present
   * in the post-mutation cart), ok=false if Shopify silently dropped it.
   *
   * The AJAX cart endpoint does not surface structured rejection reasons
   * (Shopify docs do not document any) — we can only tell "applied" from
   * "not applied" by comparing the requested code to the post-mutation
   * discount_codes list. Rich error reasons require the Storefront API
   * (Cart object, cartDiscountCodesUpdate) — pinned for future work.
   *
   * @param {string} code
   * @returns {Promise<{ok: boolean, code: string, reason?: string}>}
   */
  function applyDiscount(code) {
    ensureHydrated();
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return Promise.resolve({ ok: false, code: '', reason: 'empty' });

    if (state.appliedDiscountCodes.indexOf(normalized) !== -1) {
      return Promise.resolve({ ok: false, code: normalized, reason: 'already_applied' });
    }
    if (state.appliedDiscountCodes.length >= MAX_DISCOUNT_CODES) {
      // v1 cap — when MAX lifts (Storefront API path), this guard relaxes.
      return Promise.resolve({ ok: false, code: normalized, reason: 'cap_reached' });
    }

    const nextCodes = state.appliedDiscountCodes.concat([normalized]);
    state.appliedDiscountCodes = nextCodes;
    emit();

    // AJAX cart replaces the discount field — POST the combined set so
    // existing codes survive when stacking. Each code must be combinable
    // in admin AND the store on Shopify Advanced+ for actual stacking
    // (Shopify otherwise silently honors only the most-favorable).
    return postDiscountUpdate(nextCodes.join(',')).then((cart) => {
      if (cart) replaceFromServerInternal(cart);
      const stuck = cart && state.appliedDiscountCodes.indexOf(normalized) !== -1;
      // Only swap the drawer's SSR DOM when the cart actually changed.
      // On rejection the cart is unchanged, and re-rendering would
      // replace the <cart-manual-code> element mid-flight — its
      // post-resolve showFeedback() call would then write to a
      // detached node and the user would see no error at all.
      if (stuck) refreshCartDrawerSection(cart);
      emit();
      return stuck
        ? { ok: true, code: normalized }
        : { ok: false, code: normalized, reason: 'rejected' };
    });
  }

  /**
   * Remove a discount code. When `code` is provided, POSTs the remaining
   * codes joined by `,` so the other codes survive the round-trip (AJAX
   * cart replaces, doesn't append). When `code` is empty/omitted, clears
   * all codes by POSTing an empty discount field.
   *
   * @param {string} [code] code to remove; empty/omitted clears all
   * @returns {Promise<{ok: boolean}>}
   */
  function removeDiscount(code) {
    ensureHydrated();
    const normalized = String(code || '').trim().toUpperCase();
    const remaining = normalized
      ? state.appliedDiscountCodes.filter((c) => c !== normalized)
      : [];
    state.appliedDiscountCodes = remaining;
    emit();

    return postDiscountUpdate(remaining.join(',')).then((cart) => {
      if (cart) {
        replaceFromServerInternal(cart);
        refreshCartDrawerSection(cart);
      }
      emit();
      return { ok: true };
    });
  }

  /**
   * POST /cart/update.js with a discount value (or empty to clear) and
   * request the cart-drawer + cart-icon-bubble sections in the response
   * so the drawer's server-rendered DOM (line items, discount badges,
   * footer chips) can refresh — cart-state alone can't update those
   * (they're Liquid-rendered, not driven by the store).
   *
   * Returns the parsed JSON, or null on network/HTTP error. Errors are
   * logged and reconciled via refreshFromServer to avoid a stale
   * optimistic mutation lingering in the store.
   *
   * @param {string} code — discount code, or empty string to clear all
   * @returns {Promise<object|null>}
   */
  function postDiscountUpdate(code) {
    const body = new URLSearchParams();
    body.set('discount', String(code || ''));
    body.set('sections', 'cart-drawer,cart-icon-bubble');
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        Accept: 'application/json',
      },
      body: body.toString(),
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch((e) => {
        console.error('[cart-state] postDiscountUpdate failed', e);
        refreshFromServer();
        return null;
      });
  }

  /**
   * Hand the cart-drawer custom element a freshly-rendered section so its
   * SSR-driven content (line item discount badges, applied-chip SSR
   * fallback, etc.) reflects the post-mutation cart. The drawer's
   * renderContents() method handles the DOM swap and emits the
   * cart-drawer:hydrate event so v4 hydrators rerun.
   *
   * Coupling cart-state to the drawer DOM is a pragmatic shortcut — the
   * alternative is duplicating this in every consumer that calls
   * applyDiscount/removeDiscount. Kept narrow to one call site.
   *
   * @param {object} parsed — /cart/update.js response with `sections`
   */
  function refreshCartDrawerSection(parsed) {
    if (!parsed || !parsed.sections) return;
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      try {
        // noOpen: this is a background refresh of the drawer's SSR
        // content after a discount mutation. The user did not ask to
        // open the drawer; without this flag, every cookie auto-promote
        // and every apply/remove pops the drawer open as a side effect.
        drawer.renderContents(parsed, { noOpen: true });
      } catch (e) {
        console.error('[cart-state] cart-drawer renderContents threw', e);
      }
    }
  }

  /**
   * Swap the variant on an existing line item, addressed by stable
   * line key. Shopify's /cart/change.js endpoint does NOT support
   * changing the variant id of a line — it only updates quantity /
   * properties on an existing variant. So we remove the old line
   * (quantity:0) and add the new variant in two sequential calls.
   * The new line goes to the end of the cart; that's an accepted
   * limitation of this Shopify API path.
   *
   * The /cart/add.js call requests sections=cart-drawer,cart-icon-bubble
   * so the drawer's server-rendered content (line title, image, price
   * strikethrough, urgency line, discount badges) reflects the new
   * variant after a single round-trip — same machinery as
   * applyDiscount/removeDiscount.
   *
   * @param {string} key — Shopify line item key
   * @param {number|string} newVariantId
   * @returns {Promise<{ok: boolean, reason?: string}>}
   */
  function changeLineVariantByKey(key, newVariantId) {
    ensureHydrated();
    if (!key) return Promise.resolve({ ok: false, reason: 'invalid_line' });
    // Optimistic lines (key prefix `optimistic:`) aren't yet known to
    // the server — refuse the mutation rather than 404 at the API.
    if (key.indexOf('optimistic:') === 0) {
      return Promise.resolve({ ok: false, reason: 'pending_add' });
    }
    const idx = state.lines.findIndex((l) => l.key === key);
    if (idx === -1) return Promise.resolve({ ok: false, reason: 'invalid_line' });
    const newId = Number(newVariantId);
    if (!newId) return Promise.resolve({ ok: false, reason: 'invalid_variant' });

    const oldLine = state.lines[idx];
    const qty = Math.max(1, Number(oldLine.qty) || 1);

    // Step 1: remove the old line (quantity:0). Don't request sections
    // yet — we want a single section refresh after the add resolves
    // so the user sees one DOM swap, not two.
    const removeBody = JSON.stringify({ id: key, quantity: 0 });
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: removeBody,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((removedCart) => {
        if (!removedCart || removedCart.errors) {
          throw new Error('remove step failed');
        }
        // Step 2: add the new variant with the original quantity, asking
        // for fresh drawer/icon-bubble sections in the response.
        const addBody = JSON.stringify({
          id: newId,
          quantity: qty,
          sections: 'cart-drawer,cart-icon-bubble',
        });
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: addBody,
        }).then((r) => (r.ok ? r.json() : null));
      })
      .then((addedLine) => {
        if (!addedLine || addedLine.status || addedLine.errors) {
          // Add failed (e.g. new variant out of stock between popover
          // open and click). Roll cart-state back from the server.
          return refreshFromServer().then(() => ({ ok: false, reason: 'rejected' }));
        }
        // /cart/add.js response is the new line item, not the full cart.
        // Pull authoritative cart state via /cart.js. Sections come from
        // the add response and are passed straight to the drawer renderer.
        return refreshFromServer().then(() => {
          refreshCartDrawerSection(addedLine);
          return { ok: true };
        });
      })
      .catch((e) => {
        console.error('[cart-state] changeLineVariant failed', e);
        return refreshFromServer().then(() => ({ ok: false, reason: 'network_error' }));
      });
  }

  /**
   * Read-only snapshot of the current state. Frozen — direct mutation
   * throws in strict mode, fails silently otherwise.
   * @returns {CartState}
   */
  function getState() {
    ensureHydrated();
    return freeze(state);
  }

  /**
   * Subscribe to state changes. Returns an unsubscribe function. The
   * callback is NOT called immediately on subscribe; consumers that
   * need an initial render should call cb(getState()) themselves.
   * @param {(state: CartState) => void} cb
   * @returns {() => void}
   */
  function subscribe(cb) {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  }

  /**
   * Pull authoritative cart state from /cart.js and replace the store.
   * Used on error paths (server rejected an add or qty change) to
   * recover from a stale optimistic mutation. Returns a Promise so
   * callers can sequence follow-up work.
   * @returns {Promise<void>}
   */
  function refreshFromServer() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => { if (cart) replaceFromServer(cart); })
      .catch((e) => { console.error('[cart-state] refresh failed', e); });
  }

  global.cartState = {
    getState: getState,
    subscribe: subscribe,
    addLine: addLine,
    setQtyByKey: setQtyByKey,
    replaceFromServer: replaceFromServer,
    refreshFromServer: refreshFromServer,
    applyDiscount: applyDiscount,
    removeDiscount: removeDiscount,
    changeLineVariantByKey: changeLineVariantByKey,
    MAX_DISCOUNT_CODES: MAX_DISCOUNT_CODES,
  };
})(window);
